import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import { addScheduledProcedureCodeAction as addScheduledProcedureCode } from '../../../../../../services/EncounterService/slice';
import { useGetProceduresQuery } from '../../../../../../services/ConfigurationService';
import { useScheduledProcedureCodeAddForm } from './useScheduledProcedureCodeAddForm';
import type { ScheduledProcedureCodeType } from '../../../../../../services/EncounterService/types/encounterTypes';

interface UseScheduledProcedureCodeAddDialogProps {
  onScheduledProcedureCodeAdded?: () => void;
}

export const useScheduledProcedureCodeAddDialog = ({ onScheduledProcedureCodeAdded }: UseScheduledProcedureCodeAddDialogProps = {}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  
  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Get center ID from URL parameters
  const company = searchParams.get('company');
  const center = searchParams.get('center');
  const centerId = company && center ? `${company}-${center}` : '';

  // Fetch procedures from configuration API
  const { data: procedures = [] } = useGetProceduresQuery(
    { centerId },
    { skip: !centerId }
  );

  // Dialog open state
  const [open, setOpen] = useState(false);

  // Form state
  const formState = useScheduledProcedureCodeAddForm();

  const handleOpen = useCallback(() => {
    if (!isEncounterCancelled) {
      formState.resetForm();
      setOpen(true);
    }
  }, [formState, isEncounterCancelled]);

  const handleClose = useCallback(() => {
    setOpen(false);
    formState.resetForm();
  }, [formState]);

  const handleSubmit = useCallback(() => {
    if (!formState.validateForm()) return;

    // Calculate the next priority
    const currentCodes = encounter?.scheduledProcedureCodes || [];
    const maxPriority = currentCodes.length > 0 
      ? Math.max(...currentCodes.map(code => code.priority || 0))
      : 0;

    const newScheduledProcedureCode: ScheduledProcedureCodeType = {
      ...formState.formData,
      id: `scheduled_code_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      priority: maxPriority + 1,
    };

    dispatch(addScheduledProcedureCode(newScheduledProcedureCode));
    onScheduledProcedureCodeAdded?.();
    handleClose();
  }, [dispatch, encounter?.scheduledProcedureCodes, formState, handleClose, onScheduledProcedureCodeAdded]);

  return {
    open,
    procedures,
    isEncounterCancelled,
    handleOpen,
    handleClose,
    handleSubmit,
    ...formState,
  };
};
